/** Answers used for comparison, including records hidden by display merging. */
export type MhoAnswer = {
  qcode?: string;
  code?: string;
  snomedCode?: string;
  category?: string;
  isScore?: boolean | 'notInteractive';
  mergedWith?: readonly MhoAnswer[];
};

const CARDIAC_QUESTION_ALIASES: Readonly<Record<string, string>> = {
  Arrhythmia: 'CardiacRhythm',
  Pacemaker: 'CardiacRhythm',
  ChestPainHO: 'CardiologyHO',
  HeartAttackHO: 'CardiologyHO',
  HeartFailure: 'CardiologyHO'
};

const SCALAR_QUESTIONS = new Set([
  'Gravidity',
  'Parity',
  'Smoker',
  'Asthma',
  'GastricReflux',
  'ChronicKidneyFailureHO',
  'AlcoholConsumptionHO',
  'EpiduralHO',
  'Braces'
]);

/** These groups use a choice code and its `.NO` counterpart for one fact. */
const FACT_GROUPS = new Set([
  'CardiacRhythm',
  'CardiologyHO',
  'CurrentInfections',
  'StomachProblemHO',
  'MetabolicQuestions',
  'PreviousPregnanciesHO',
  'VisionProblems',
  'HeartAttackHODetails'
]);

const YES_NO_ANSWERS = new Set(['YES', 'NO', 'IDK']);

function normalizeAnswer(answer: MhoAnswer): MhoAnswer {
  const { qcode, code } = answer;
  if (qcode === 'CurrentInfectionsFR') {
    return { ...answer, qcode: 'CurrentInfections' };
  }
  if (
    qcode &&
    CARDIAC_QUESTION_ALIASES[qcode] &&
    (code === qcode || code === `${qcode}.NO`)
  ) {
    return { ...answer, qcode: CARDIAC_QUESTION_ALIASES[qcode] };
  }
  return answer;
}

/** Builds a read-only check from recorded answers, independently of the catalog. */
export function createMhoAnswerCheck(
  answers: readonly MhoAnswer[]
): (candidate: MhoAnswer) => boolean {
  const byQuestion = new Map<string, MhoAnswer[]>();
  const uncoded: MhoAnswer[] = [];
  const snomeds = new Set<string>();

  const collect = (items: readonly MhoAnswer[]): void => {
    for (const item of items) {
      const answer = normalizeAnswer(item);
      if (answer.qcode) {
        const group = byQuestion.get(answer.qcode) ?? [];
        group.push(answer);
        byQuestion.set(answer.qcode, group);
      } else {
        uncoded.push(answer);
      }
      if (answer.snomedCode) {
        snomeds.add(`${answer.category ?? ''}___${answer.snomedCode}`);
      }
      collect(item.mergedWith ?? []);
    }
  };
  collect(answers);

  return (candidate): boolean => {
    const answer = normalizeAnswer(candidate);
    const { qcode, code, category, snomedCode } = answer;
    if (snomedCode && snomeds.has(`${category ?? ''}___${snomedCode}`)) {
      return true;
    }
    if (!qcode) {
      return (
        !snomedCode &&
        code != null &&
        uncoded.some((item) => item.category === category && item.code === code)
      );
    }
    // Smoking display merges hide the main answer behind its detail questions.
    if (
      qcode === 'Smoker' &&
      [...byQuestion.keys()].some((existing) => existing.startsWith('Smoker'))
    ) {
      return true;
    }
    // Questionnaire processing omits SleepApnea when CPAP is already confirmed.
    if (
      qcode === 'SleepApnea' &&
      byQuestion.get('CpapMachine')?.some((item) => item.code === 'YES')
    ) {
      return true;
    }

    const existing = byQuestion.get(qcode) ?? [];
    if (answer.isScore) {
      return existing.some((item) => !!item.isScore);
    }
    if (SCALAR_QUESTIONS.has(qcode) || code == null || code === 'NONE') {
      return existing.length > 0;
    }
    return existing.some((item) => {
      if (item.isScore) {
        return false;
      }
      if (item.code === code || item.code === 'IDK' || item.code === 'NONE') {
        return true;
      }
      if (YES_NO_ANSWERS.has(code) && YES_NO_ANSWERS.has(item.code ?? '')) {
        return true;
      }
      return (
        FACT_GROUPS.has(qcode) &&
        (item.code === `${code}.NO` || code === `${item.code}.NO`)
      );
    });
  };
}

/** Also checks earlier defaults so one Clear request cannot repeat a covered fact. */
export function getUnansweredMhoDefaults<T extends MhoAnswer>(
  defaults: readonly T[],
  answers: readonly MhoAnswer[]
): T[] {
  const hasAnswer = createMhoAnswerCheck(answers);
  const additions: T[] = [];
  for (const candidate of defaults) {
    if (!hasAnswer(candidate) && !createMhoAnswerCheck(additions)(candidate)) {
      additions.push(candidate);
    }
  }
  return additions;
}
